import { fetchRestApiEndpoint } from './utils/fetchRestApiEndpoint';
import { Methods } from '../../types.d';
import { log } from '@/utils/logger';

export interface fetchPostProps {
  url: string;
  headers?: Record<string, string>[] | object;
  body: Record<string, unknown>;
}

/**
 * Performs a POST request to a REST API endpoint with enhanced logging
 *
 * @param params - The request parameters
 * @param params.url - The endpoint URL to POST to
 * @param params.headers - Optional HTTP headers to include in the request
 * @param params.body - The request body data to be JSON stringified
 *
 * @returns Promise that resolves to the JSON response data
 *
 * @throws {Error} When the response is not ok or JSON parsing fails
 *
 * @example
 * ```typescript
 * const result = await fetchPost({
 *   url: '/api/users',
 *   body: { name: 'John', email: 'john@example.com' },
 *   headers: { 'Authorization': 'Bearer token' }
 * });
 * ```
 */
export const fetchPost = async ({
  url,
  headers = {},
  body,
}: fetchPostProps) => {
  const options = {
    method: Methods.POST,
    redirect: 'follow' as RequestRedirect,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(headers as Record<string, string> || {}),
    },
    body: JSON.stringify(body),
  };

  try {
    const response = await fetchRestApiEndpoint(url, options);

    if (!response) {
      log.throw({
        event: 'fetching BE',
        message: 'No response',
        stringyfy: { url, options },
      });
      return;
    }

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const json: RestPayload = await response.json();
    return json;
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    const isJsonError = error.message?.includes('JSON') || error.name === 'SyntaxError';
    log.throw({
      event: 'fetching BE',
      message: isJsonError ? 'Response is not JSON' : errorObj.message || 'Error fetching BE',
      stringyfy: { url, options, error: errorObj },
    });
  }
};